import { useRef, useState, type ReactNode } from 'react';
import { Anchor, Badge, Button, Code, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { Link } from 'react-router-dom';
import {
  useCallableContext,
  useCompositionVersion,
} from '@/hooks/api/automations/useExecutionCompositions.ts';
import { useSuiteDetail } from '@/hooks/api/automationSuites/useAutomationSuites.ts';
import type { CompositionRecord, ExactVersion, Location } from '@/types/executionCompositions.ts';
import { pathLabel } from './compositionPresentation.ts';
import classes from './CompositionPresentation.module.css';

export function ReleaseSection({
  title,
  children,
  description,
}: {
  title: string;
  children: ReactNode;
  description?: string;
}) {
  return (
    <Paper component="section" withBorder p="md" className={classes.section} aria-label={title}>
      <Stack gap="md">
        <div>
          <Title order={3}>{title}</Title>
          {description && (
            <Text size="sm" c="dimmed">
              {description}
            </Text>
          )}
        </div>
        {children}
      </Stack>
    </Paper>
  );
}

/** Presentation only. Missing metadata never changes an exact selection. */
export function ImplementationLabel({ version }: { version: ExactVersion }) {
  const metadata = useCompositionVersion(version.automation_version_id);
  const exact =
    metadata.isFetchedAfterMount && !metadata.isError && !metadata.isFetching
      ? metadata.data
      : undefined;
  return (
    <span>
      {exact?.name || `Automation name unavailable (${version.automation_id})`}
      {' · '}
      {exact?.version_number != null
        ? `v${exact.version_number}`
        : `Exact version ${version.automation_version_id}`}
    </span>
  );
}

export function TriggerLabel({ versionId, triggerId }: { versionId: string; triggerId: string }) {
  const context = useCallableContext(versionId);
  const trigger =
    context.isFetchedAfterMount && !context.isError && !context.isFetching
      ? context.data?.triggers.find((item) => item.step_id === triggerId)
      : undefined;
  return <span>{trigger?.name || `Trigger name unavailable (${triggerId})`}</span>;
}

export function LocationLabel({ location }: { location: Location }) {
  // The full qualified call path remains visible: repeated calls must stay distinguishable.
  return (
    <span>
      <ImplementationLabel version={location.root} />
      {' · '}
      <TriggerLabel
        versionId={location.root.automation_version_id}
        triggerId={location.root.trigger_step_id}
      />
      {location.call_chain.length > 0 && ` → ${location.call_chain.map(pathLabel).join(' → ')}`}
    </span>
  );
}

export function SuiteLink({ suiteId }: { suiteId: string }) {
  const suite = useSuiteDetail(suiteId, 1);
  const name =
    suite.isFetchedAfterMount && !suite.isError && !suite.isFetching ? suite.data?.name : undefined;
  return (
    <Anchor component={Link} to={`/automation-suites/${suiteId}`}>
      {name || `Suite name unavailable (${suiteId})`}
    </Anchor>
  );
}

export function ExactDetails({
  values,
  summary = 'Exact details',
}: {
  values: Record<string, string | null | undefined>;
  summary?: string;
}) {
  const copyRequest = useRef(0);
  const [copyResult, setCopyResult] = useState<{
    label: string;
    value: string;
    failed: boolean;
  }>();
  const currentResult =
    copyResult && values[copyResult.label] === copyResult.value ? copyResult : undefined;
  return (
    <details className={classes.details}>
      <summary>{summary}</summary>
      <Stack gap="xs" mt="xs">
        {Object.entries(values).map(([label, value]) => (
          <div key={label}>
            <Group gap="xs" justify="space-between">
              <Text size="sm" fw={500}>
                {label}
              </Text>
              {value && (
                <Button
                  size="compact-xs"
                  variant="subtle"
                  onClick={async () => {
                    const request = ++copyRequest.current;
                    try {
                      await navigator.clipboard.writeText(value);
                      if (request === copyRequest.current)
                        setCopyResult({ label, value, failed: false });
                    } catch {
                      if (request === copyRequest.current)
                        setCopyResult({ label, value, failed: true });
                    }
                  }}
                  aria-label={`Copy ${label}`}
                >
                  Copy
                </Button>
              )}
            </Group>
            <Code className={classes.identity}>{value ?? 'Unavailable'}</Code>
          </div>
        ))}
        <Text size="xs" role="status">
          {currentResult?.failed
            ? 'Could not copy. Select the exact value to copy it manually.'
            : currentResult
              ? `${currentResult.label} copied.`
              : ''}
        </Text>
      </Stack>
    </details>
  );
}

export function SavedCandidateSummary({ record }: { record: CompositionRecord }) {
  return (
    <Paper withBorder p="sm" className={classes.section}>
      <Stack gap="xs">
        <Group>
          <Text fw={600}>Saved release configuration</Text>
          <Badge>{record.status}</Badge>
        </Group>
        <Text size="sm">Test evidence and review use this saved selection.</Text>
        {record.candidate.roots.map((root) => (
          <Text key={`${root.automation_version_id}/${root.trigger_step_id}`} size="sm">
            <ImplementationLabel version={root} />
            {' · '}
            <TriggerLabel versionId={root.automation_version_id} triggerId={root.trigger_step_id} />
          </Text>
        ))}
        <ExactDetails
          values={{
            'Release configuration ID': record.record.release_id,
            'Execution hash': record.record.identity.composition_digest,
            'Resource hash': record.record.identity.resource_digest,
            'Documentation hash': record.record.identity.documentation_digest,
            'Requirements hash': record.record.identity.requirements_digest,
          }}
        />
      </Stack>
    </Paper>
  );
}
